// src/components/DomusAIChatWidget.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaRobot,
  FaLock,
  FaBox, 
  FaPaperPlane, 
  FaTimes, 
  FaMinus, 
  FaTrashAlt, 
  FaSwimmingPool, 
  FaMoneyBillWave, 
  FaQrcode, 
  FaCar, 
  FaComments,
  FaShieldAlt,
  FaPaperclip,
  FaUpload,
  FaCreditCard,
  FaSpinner,
  FaReceipt,
  FaEye,
  FaCheck,
  FaFileAlt,
  FaExpandAlt,
  FaCompressAlt,
  FaThumbsUp,
  FaThumbsDown,
  FaCopy,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useModulos } from '../hooks/useModulos';
import { enviarMensajeChatWeb, registrarComprobantePago, calificarRespuestaChat } from '../services/domusAiService';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import './DomusAIChatWidget.css';

const SUGGESTIONS = [
  { id: 'saldo', label: 'Mi saldo y cuota', icon: FaMoneyBillWave, query: '¿Cuál es mi saldo actual y cuánto debo pagar?' },
  { id: 'comprobante', label: '📤 Subir comprobante', icon: FaUpload, isUpload: true },
  { id: 'pass', label: 'Cambiar contraseña', icon: FaLock, query: '¿Cómo puedo cambiar mi contraseña de acceso?' },
  { id: 'paqueteria', label: 'Paquetería en caseta', icon: FaBox, query: '¿Cómo sé si tengo paquetería pendiente en caseta?' },
  { id: 'amenidades', label: 'Amenidades y horarios', icon: FaSwimmingPool, query: '¿Cuáles son las amenidades activas y sus horarios?' },
  { id: 'qr', label: 'Pase QR para visita', icon: FaQrcode, query: '¿Cómo genero un código QR para un invitado o visitante?' },
  { id: 'chat', label: 'Chat Comunitario', icon: FaComments, query: '¿Dónde encuentro el chat comunitario para hablar con mis vecinos?' },
  { id: 'reglas', label: 'Reglas de circulación', icon: FaCar, query: '¿Cuáles son las reglas de circulación en el fraccionamiento?' }
];

const SUGGESTIONS_ADMIN = [
  { id: 'comprobantes_pend', label: '📥 Comprobantes por validar', icon: FaReceipt, query: '¿Cuáles son los comprobantes de pago pendientes de validar?' },
  { id: 'resumen_global', label: '📊 Resumen Financiero', icon: FaMoneyBillWave, query: 'hola, ¿cuál es el estado financiero del fraccionamiento?' },
  { id: 'amenidades_admin', label: 'Amenidades y horarios', icon: FaSwimmingPool, query: '¿Cuáles son las amenidades activas y sus horarios?' },
  { id: 'qr_admin', label: 'Pases QR de visitas', icon: FaQrcode, query: '¿Cómo genero un código QR para un invitado o visitante?' }
];

const DomusAIChatWidget = ({ fraccionamientoId: propFraccId }) => {
  const navigate = useNavigate();
  const { userData, fraccionamientoId: activeFraccId } = useAuth();
  const fraccionamientoId = propFraccId || activeFraccId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
  const { modulos, loading: loadingModulos } = useModulos(fraccionamientoId);

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [comprobantesPendientes, setComprobantesPendientes] = useState([]);
  const [mostrarComprobantesBanner, setMostrarComprobantesBanner] = useState(true);
  const [modalZoomImg, setModalZoomImg] = useState(null);
  const [calificaciones, setCalificaciones] = useState({});

  const rolLower = String(userData?.rol || '').toLowerCase().trim();
  const esAdmin = ['superadmin', 'admin', 'platformadmin', 'manager', 'admin_fracc'].includes(rolLower);

  const nombreUsuario = userData?.nombre || userData?.nombreCompleto || 'Vecino(a)';
  const fraccNombre = userData?.fraccionamientoNombre || 'tu fraccionamiento';
  const casaUsuario = userData?.casa || userData?.numeroCasa || userData?.lote || userData?.dpto || '';

  const initialWelcomeMsg = {
    id: 'welcome',
    sender: 'bot',
    text: esAdmin
      ? `¡Hola, **${nombreUsuario}**! 👋 Soy tu **Asistente Virtual IA** de Domus Habita.\n\nEstoy conectado a la administración de **${fraccNombre}** para apoyarte en:\n• 📥 Notificación y conciliación de comprobantes de pago.\n• 📊 Resumen financiero global y búsqueda de adeudos por casa.\n• 💳 Captura rápida de ingresos.\n• 🏊 Consulta de amenidades y normas del condominio.\n\n¿En qué te puedo apoyar el día de hoy?`
      : `¡Hola, **${nombreUsuario}**! 👋 Soy tu **Asistente Virtual IA** de Domus Habita.\n\nEstoy aquí para ayudarte en la operación de **${fraccNombre}**:\n• 💰 Consultar tu saldo y desgloses de cuotas.\n• 📤 Reportar y adjuntar comprobantes de pago.\n• 🏊 Horarios de amenidades activas.\n• 🎟️ Guía y requisitos para pases de visita QR.\n• 💬 Canales del Chat Comunitario.\n\n¿En qué te puedo apoyar el día de hoy?`,
    timestamp: new Date()
  };

  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`domus_chat_${fraccionamientoId}_${userData?.uid}`);
      return saved ? JSON.parse(saved) : [initialWelcomeMsg];
    } catch {
      return [initialWelcomeMsg];
    }
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const widgetContainerRef = useRef(null);
  const location = useLocation();

  // Auto-scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      setHasUnread(false);
    }
  }, [messages, isOpen, isMinimized]);

  // Colapsar automáticamente al cambiar de ruta para no interponerse en la nueva pantalla
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
      setIsMinimized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Cerrar/colapsar al hacer clic o tap fuera del chat (especialmente útil en móvil para despejar menú lateral y modales)
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (widgetContainerRef.current && !widgetContainerRef.current.contains(e.target)) {
        // No cerrar si el usuario está interactuando con modales de sweetalert2 o lightbox de comprobantes
        if (e.target.closest && (e.target.closest('.swal2-container') || e.target.closest('.domus-ai-modal-zoom-overlay'))) {
          return;
        }
        setIsOpen(false);
        setIsMinimized(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [isOpen]);

  // Persistir mensajes en sessionStorage de la sesión actual
  useEffect(() => {
    if (fraccionamientoId && userData?.uid) {
      try {
        sessionStorage.setItem(`domus_chat_${fraccionamientoId}_${userData.uid}`, JSON.stringify(messages));
      } catch (err) {
        console.warn('Error guardando historial de chat en session:', err);
      }
    }
  }, [messages, fraccionamientoId, userData]);

  // 🔔 Escuchar comprobantes de pago pendientes en tiempo real para Administradores
  useEffect(() => {
    if (!esAdmin || !fraccionamientoId) return;

    try {
      const compsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'comprobantesPago');
      const q = query(compsRef, where('estado', '==', 'pendiente_validacion'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })).sort((a, b) => {
          const tA = a.fecha?.toMillis ? a.fecha.toMillis() : (a.fecha?.seconds ? a.fecha.seconds * 1000 : 0);
          const tB = b.fecha?.toMillis ? b.fecha.toMillis() : (b.fecha?.seconds ? b.fecha.seconds * 1000 : 0);
          return tB - tA;
        });

        setComprobantesPendientes(docs);
        if (docs.length > 0 && !isOpen) {
          setHasUnread(true);
        }
      }, (err) => {
        console.warn('Aviso escuchando comprobantes pendientes:', err.message);
      });

      return () => unsubscribe();
    } catch (errSub) {
      console.warn('Error configurando onSnapshot comprobantes:', errSub);
    }
  }, [esAdmin, fraccionamientoId, isOpen]);

  // Si el módulo está apagado para este fraccionamiento, no renderizar
  if (loadingModulos || modulos.comunidad === false || modulos.asistente_virtual_ia === false) {
    return null;
  }

  const handleSendMessage = async (textToSend) => {
    const text = (textToSend || inputValue).trim();
    if (!text || isTyping) return;

    const userMsg = {
      id: `usr_${Date.now()}`,
      sender: 'user',
      text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    // Preparar historial para enviar contexto
    const historialContexto = messages.slice(-4).map(m => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      text: m.text
    }));

    try {
      const resp = await enviarMensajeChatWeb({
        mensaje: text,
        usuario: {
          uid: userData?.uid,
          nombre: nombreUsuario,
          casa: casaUsuario,
          casaId: userData?.casaId || userData?.casaDocId || null,
          rol: userData?.rol || 'residente',
          fraccionamientoId,
          fraccionamientoNombre: fraccNombre,
          telefono: userData?.telefono || userData?.celular || ''
        },
        historial: historialContexto
      });

      const botMsg = {
        id: `bot_${Date.now()}`,
        sender: 'bot',
        text: resp.respuesta || 'Disculpa, no pude procesar la consulta en este momento.',
        sugerencias: Array.isArray(resp.sugerencias) ? resp.sugerencias : [],
        preguntaOriginal: text,
        logId: resp.logId || null,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Error enviando mensaje al Asistente IA:', error);
      const errorMsg = {
        id: `bot_err_${Date.now()}`,
        sender: 'bot',
        text: 'Ocurrió una interrupción de conexión con el Asistente IA. Por favor intenta enviar tu mensaje nuevamente.',
        sugerencias: [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
      if (!isOpen) {
        setHasUnread(true);
      }
    }
  };

  const handleCopiarMensaje = (texto) => {
    const textoLimpio = String(texto || '')
      .replace(/\[ACTION:[^\]]+\]/g, '')
      .trim();

    navigator.clipboard?.writeText(textoLimpio);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Respuesta copiada',
      showConfirmButton: false,
      timer: 1500
    });
  };

  const handleCalificar = async (msg, tipo) => {
    if (calificaciones[msg.id]) return;

    if (tipo === 'buena') {
      setCalificaciones(prev => ({ ...prev, [msg.id]: 'buena' }));
      await calificarRespuestaChat({
        mensajeId: msg.id,
        pregunta: msg.preguntaOriginal || '',
        respuesta: msg.text,
        calificacion: 'buena',
        comentario: '',
        usuarioUid: userData?.uid || null,
        nombreUsuario,
        casa: casaUsuario,
        rol: userData?.rol || 'residente',
        fraccionamientoId,
        fraccionamientoNombre: fraccNombre
      });
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: '¡Gracias por tu calificación! 👍',
        showConfirmButton: false,
        timer: 1800
      });
    } else {
      const { value: comentario, isConfirmed } = await Swal.fire({
        title: 'Calificar respuesta',
        input: 'textarea',
        inputLabel: '¿Qué información faltó o estuvo incorrecta? (Opcional)',
        inputPlaceholder: 'Escribe tu retroalimentación para calibrar el asistente...',
        showCancelButton: true,
        confirmButtonText: 'Enviar calificación',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444'
      });

      if (!isConfirmed) return;

      setCalificaciones(prev => ({ ...prev, [msg.id]: 'mala' }));
      await calificarRespuestaChat({
        mensajeId: msg.id,
        pregunta: msg.preguntaOriginal || '',
        respuesta: msg.text,
        calificacion: 'mala',
        comentario: comentario || 'Sin comentario',
        usuarioUid: userData?.uid || null,
        nombreUsuario,
        casa: casaUsuario,
        rol: userData?.rol || 'residente',
        fraccionamientoId,
        fraccionamientoNombre: fraccNombre
      });
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: 'Retroalimentación recibida',
        showConfirmButton: false,
        timer: 2000
      });
    }
  };

  const handleAccionSugerencia = (sug) => {
    if (!sug) return;
    if (sug.tipo === 'navegacion' && sug.ruta) {
      navigate(sug.ruta);
      if (window.innerWidth <= 768) {
        setIsOpen(false);
      }
    } else if (sug.tipo === 'accion') {
      if (sug.accion === 'abrir_upload') {
        fileInputRef.current?.click();
      } else if (sug.accion === 'capturar_pago' && sug.casa) {
        handleAbrirModalPago(sug.casa);
      }
    } else {
      handleSendMessage(sug.consulta || sug.texto);
    }
  };

  // Carga de Comprobante de Pago desde el widget
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo demasiado grande',
        text: 'El comprobante no debe superar los 10 MB.',
        confirmButtonColor: '#4f46e5'
      });
      return;
    }

    setIsUploading(true);

    const uploadMsg = {
      id: `usr_file_${Date.now()}`,
      sender: 'user',
      text: `📤 *Comprobante de pago adjuntado:* ${file.name}`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, uploadMsg]);

    try {
      const extension = file.name.split('.').pop() || 'png';
      const storagePath = `comprobantes_pago/${fraccionamientoId}/${casaUsuario || 'sin_casa'}_${Date.now()}.${extension}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      await registrarComprobantePago({
        fraccionamientoId,
        casa: casaUsuario || 'N/A',
        residente: nombreUsuario,
        archivoUrl: downloadUrl,
        usuarioUid: userData?.uid || null,
        notas: `Archivo: ${file.name}`
      });

      const botConfirmMsg = {
        id: `bot_file_ok_${Date.now()}`,
        sender: 'bot',
        text: `¡Comprobante recibido con éxito, **${nombreUsuario}**! 🎉\n\nHe notificado formalmente a la administración de **${fraccNombre}** adjuntando tu comprobante para que validen tu abono y actualicen el saldo de tu Casa **${casaUsuario || ''}**.\n\nEn cuanto el administrador capture tu pago, verás la actualización en tu Estado de Cuenta.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botConfirmMsg]);
    } catch (uploadError) {
      console.error('Error subiendo comprobante en widget:', uploadError);
      const botErrMsg = {
        id: `bot_file_err_${Date.now()}`,
        sender: 'bot',
        text: 'Lo siento, ocurrió un error al subir tu comprobante. Por favor intenta subirlo nuevamente o utiliza el botón "Subir Comprobante" en la sección de Finanzas.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botErrMsg]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([initialWelcomeMsg]);
  };

  const handleAbrirModalPago = (target) => {
    const isObj = typeof target === 'object' && target !== null;
    const casaNumero = isObj ? (target.casa || target.casaNumero) : target;
    const montoSugerido = isObj ? (target.montoReportado || target.monto || target.adeudoSugerido || 0) : 0;
    const comprobanteId = isObj ? (target.id || target.comprobanteId || null) : null;
    const comprobanteUrl = isObj ? (target.archivoUrl || target.comprobanteUrl || null) : null;
    const nombreArchivo = isObj ? (target.nombreArchivo || null) : null;
    const notas = isObj ? (target.notas || 'Comprobante reportado vía chatbot') : 'Cobro vía chatbot';

    window.dispatchEvent(new CustomEvent('domus_abrir_modal_pago', {
      detail: {
        casaNumero: String(casaNumero),
        montoSugerido: Number(montoSugerido) || 0,
        comprobanteId,
        comprobanteUrl,
        nombreArchivo,
        notas
      }
    }));
  };

  const handleMarcarConciliado = async (comprobanteId, casaNumero) => {
    try {
      const compRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comprobantesPago', comprobanteId);
      await updateDoc(compRef, {
        estado: 'conciliado',
        fechaConciliacion: new Date(),
        conciliadoPor: userData?.nombre || userData?.displayName || 'Administrador'
      });
      Swal.fire({
        icon: 'success',
        title: '¡Comprobante Conciliado!',
        text: `El comprobante de Casa ${casaNumero} fue marcado como validado.`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error al conciliar comprobante:', err);
    }
  };

  const esImagen = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.png') || lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.webp') || lower.includes('.gif') || (!lower.includes('.pdf') && lower.includes('alt=media'));
  };

  // Renderizador limpio de texto con extracción de comprobantes y acciones (SIN URLs CRUDAS)
  const formatMessageText = (rawText) => {
    if (!rawText) return '';

    let cleanText = String(rawText);

    // 1. Extraer acciones de tarjetas de comprobante: [ACTION:COMPROBANTE_CARD:id:casa:residente:monto:nombreArchivo:archivoUrl]
    const comprobanteCards = [];
    const compRegex = /\[ACTION:COMPROBANTE_CARD:([^:]+):([^:]+):([^:]+):([^:]+):([^:]+):([^\]]+)\]/g;
    let compMatch;
    while ((compMatch = compRegex.exec(cleanText)) !== null) {
      try {
        comprobanteCards.push({
          id: compMatch[1],
          casa: compMatch[2],
          residente: decodeURIComponent(compMatch[3]),
          monto: Number(compMatch[4]) || 0,
          nombreArchivo: decodeURIComponent(compMatch[5]),
          archivoUrl: decodeURIComponent(compMatch[6])
        });
      } catch (eDec) {
        console.warn('Error decodificando comprobante card:', eDec);
      }
    }
    cleanText = cleanText.replace(compRegex, '');

    // 2. Extraer acción de captura de pago si existe: [ACTION:CAPTURAR_PAGO:XXX]
    let actionCasa = null;
    cleanText = cleanText.replace(/\[ACTION:CAPTURAR_PAGO:([a-zA-Z0-9_-]+)\]/g, (match, casa) => {
      actionCasa = casa;
      return '';
    });

    // 3. Ocultar enlaces crudos de storage si existieran en el texto
    cleanText = cleanText.replace(/https:\/\/firebasestorage\.googleapis\.com[^\s)\]]+/g, '');
    cleanText = cleanText.replace(/\[Ver Comprobante\]\(\)/g, '');

    // 4. Des-escapar \n literal a salto de línea real
    cleanText = cleanText.replace(/\\n/g, '\n').trim();
    const lines = cleanText.split('\n');

    return (
      <>
        {lines.map((line, lineIndex) => {
          const renderLine = (str) => {
            const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = linkRegex.exec(str)) !== null) {
              if (match.index > lastIndex) {
                parts.push(str.substring(lastIndex, match.index));
              }
              const linkText = match[1];
              const linkUrl = match[2];
              parts.push(
                <a 
                  key={match.index} 
                  href={linkUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="domus-ai-chat-link"
                >
                  {linkText}
                </a>
              );
              lastIndex = linkRegex.lastIndex;
            }

            if (lastIndex < str.length) {
              parts.push(str.substring(lastIndex));
            }

            return parts.map((part, pIdx) => {
              if (typeof part !== 'string') return part;
              const bParts = part.split(/(\*\*.*?\*\*)/g);
              return bParts.map((bp, bpIdx) => {
                if (bp.startsWith('**') && bp.endsWith('**')) {
                  return <strong key={bpIdx}>{bp.slice(2, -2)}</strong>;
                }
                return bp;
              });
            });
          };

          return (
            <React.Fragment key={lineIndex}>
              {renderLine(line)}
              {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}

        {/* Tarjetas de Comprobante Visuales Incrustadas */}
        {comprobanteCards.map((card, cIdx) => (
          <div key={card.id || cIdx} className="domus-ai-embedded-receipt-card">
            <div className="embedded-receipt-header">
              <span className="badge-comprobante-header">📥 Comprobante por Validar</span>
              <span className="badge-casa-pill">Casa {card.casa}</span>
            </div>
            <div className="embedded-receipt-body">
              <div className="embedded-receipt-resident">👤 <strong>{card.residente}</strong></div>
              <div className="embedded-receipt-row">
                <span className="label">Monto a validar:</span>
                <span className="amount">${Number(card.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
              </div>
              <div className="embedded-receipt-row">
                <span className="label">Archivo adjunto:</span>
                <span className="filename">{card.nombreArchivo}</span>
              </div>
            </div>

            {/* Vista Previa Visual del Comprobante - SIN URLs */}
            {card.archivoUrl && (
              <div className="embedded-receipt-preview">
                {esImagen(card.archivoUrl) ? (
                  <div 
                    className="embedded-receipt-img-wrap"
                    onClick={() => setModalZoomImg(card.archivoUrl)}
                    title="Clic para inspeccionar comprobante en tamaño completo"
                  >
                    <img src={card.archivoUrl} alt={`Comprobante Casa ${card.casa}`} className="embedded-receipt-img" />
                    <div className="embedded-receipt-img-overlay">
                      <FaEye /> <span>Inspeccionar Comprobante</span>
                    </div>
                  </div>
                ) : (
                  <a href={card.archivoUrl} target="_blank" rel="noopener noreferrer" className="embedded-receipt-pdf-btn">
                    <FaFileAlt /> <span>Abrir comprobante PDF</span>
                  </a>
                )}
              </div>
            )}

            <div className="embedded-receipt-actions">
              <button
                type="button"
                className="domus-ai-btn-capturar-pago"
                onClick={() => handleAbrirModalPago(card)}
                title={`Capturar pago para Casa ${card.casa}`}
              >
                <FaCreditCard /> Capturar Pago Casa {card.casa}
              </button>
              <button
                type="button"
                className="domus-ai-btn-conciliado"
                onClick={() => handleMarcarConciliado(card.id, card.casa)}
                title="Marcar como comprobante conciliado"
              >
                <FaCheck /> Conciliado
              </button>
            </div>
          </div>
        ))}

        {/* Botón interactivo de Captura de Pago si no vino en tarjeta */}
        {actionCasa && comprobanteCards.length === 0 && (
          <div className="domus-ai-action-btn-wrapper">
            <button
              type="button"
              className="domus-ai-btn-capturar-pago"
              onClick={() => handleAbrirModalPago(actionCasa)}
              title={`Capturar pago para Casa ${actionCasa}`}
            >
              <FaCreditCard /> Capturar Pago Casa {actionCasa}
            </button>
          </div>
        )}
      </>
    );
  };
return (
    <aside 
      ref={widgetContainerRef} 
      className={`domus-ai-widget-container ${isOpen ? 'is-open' : ''}`} 
      aria-label="Asistente Virtual IA Domus Habita"
    >
      {/* Input de archivo oculto para comprobantes */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* Botón Flotante Launcher */}
      {!isOpen && (
        <button
          className={`domus-ai-launcher ${hasUnread ? 'has-unread' : ''}`}
          onClick={() => { setIsOpen(true); setIsMinimized(false); }}
          aria-label="Abrir Asistente Virtual Inteligente"
          title="Asistente Virtual Domus Habita"
        >
          <div className="domus-ai-launcher-icon">
            <FaRobot />
          </div>
          <span className="domus-ai-launcher-label">Asistente IA</span>
          {hasUnread && <span className="domus-ai-unread-badge" />}
        </button>
      )}

      {/* Ventana de Chat Expandible */}
      {isOpen && (
        <div className={`domus-ai-window ${esAdmin ? 'admin-mode' : ''} ${isExpanded ? 'expanded' : ''} ${isMinimized ? 'minimized' : ''}`}>
          {/* Header */}
          <div className="domus-ai-header">
            <div className="domus-ai-header-info">
              <div className="domus-ai-avatar">
                <FaRobot />
                <span className="domus-ai-online-dot" />
              </div>
              <div className="domus-ai-header-titles">
                <h4>Asistente Domus Habita</h4>
                <p>
                  {esAdmin ? (
                    <span>Modo Administración</span>
                  ) : rolLower === 'caseta' ? (
                    <span>Terminal de Caseta</span>
                  ) : casaUsuario ? (
                    <span>Casa {casaUsuario}</span>
                  ) : (
                    <span>{fraccNombre || 'Residente'}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="domus-ai-header-actions">
              <button 
                type="button" 
                className="domus-ai-btn-icon" 
                onClick={handleClearChat} 
                title="Limpiar conversación"
                aria-label="Limpiar conversación"
              >
                <FaTrashAlt />
              </button>
              <button 
                type="button" 
                className="domus-ai-btn-icon" 
                onClick={() => setIsExpanded(!isExpanded)} 
                title={isExpanded ? "Restaurar tamaño" : "Expandir ventana"}
                aria-label={isExpanded ? "Restaurar tamaño" : "Expandir ventana"}
              >
                {isExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
              </button>
              <button 
                type="button" 
                className="domus-ai-btn-icon" 
                onClick={() => {
                  if (window.innerWidth <= 768) {
                    setIsOpen(false);
                    setIsMinimized(false);
                  } else {
                    setIsMinimized(!isMinimized);
                  }
                }} 
                title={isMinimized ? "Maximizar" : "Minimizar"}
                aria-label={isMinimized ? "Maximizar" : "Minimizar"}
              >
                <FaMinus />
              </button>
              <button 
                type="button" 
                className="domus-ai-btn-icon close-btn" 
                onClick={() => setIsOpen(false)} 
                title="Cerrar asistente"
                aria-label="Cerrar asistente"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Cuerpo del Chat (si no está minimizado) */}
          {!isMinimized && (
            <>
              {/* BANNER PROACTIVO DE COMPROBANTES PENDIENTES PARA ADMINISTRADORES */}
              {esAdmin && comprobantesPendientes.length > 0 && (
                <div className="domus-ai-pending-banner">
                  <div className="domus-ai-pending-header">
                    <div className="domus-ai-pending-title">
                      <FaReceipt className="domus-ai-pending-icon" />
                      <span>Comprobantes por Conciliar ({comprobantesPendientes.length})</span>
                    </div>
                    <button
                      type="button"
                      className="domus-ai-pending-toggle"
                      onClick={() => setMostrarComprobantesBanner(!mostrarComprobantesBanner)}
                    >
                      {mostrarComprobantesBanner ? 'Ocultar' : 'Ver'}
                    </button>
                  </div>

                  {mostrarComprobantesBanner && (
                    <div className="domus-ai-pending-scroll">
                      {comprobantesPendientes.map(comp => (
                        <div key={comp.id} className="domus-ai-pending-card">
                          <div className="pending-card-top">
                            <span className="pending-casa-badge">Casa {comp.casa}</span>
                            <span className="pending-residente-name">{comp.residente}</span>
                          </div>
                          <div className="pending-card-info-row">
                            <span className="pending-label">Monto a conciliar:</span>
                            <span className="pending-amount">
                              ${Number(comp.montoReportado || comp.adeudoSugerido || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                            </span>
                          </div>
                          {comp.montoReportado > 0 && comp.adeudoSugerido > 0 && Math.abs(comp.montoReportado - comp.adeudoSugerido) > 1 && (
                            <div className="pending-card-monto-alert" title="El residente tecleó un monto diferente a su adeudo registrado">
                              ⚠️ Reportado por residente: ${Number(comp.montoReportado).toFixed(2)} | Adeudo registrado: ${Number(comp.adeudoSugerido).toFixed(2)}
                            </div>
                          )}
                          {comp.notas && (
                            <div className="pending-card-info-row">
                              <span className="pending-label">Nota:</span>
                              <span className="pending-nota-text" title={comp.notas}>{comp.notas}</span>
                            </div>
                          )}
                          <div className="pending-card-info-row">
                            <span className="pending-label">Archivo:</span>
                            <span className="pending-filename">{comp.nombreArchivo || 'comprobante'}</span>
                          </div>

                          {/* Previsualización visual del comprobante - SIN URLs */}
                          {comp.archivoUrl && (
                            <div className="pending-card-thumb-wrap">
                              {esImagen(comp.archivoUrl) ? (
                                <div 
                                  className="pending-thumb-box"
                                  onClick={() => setModalZoomImg(comp.archivoUrl)}
                                  title="Clic para inspeccionar comprobante en alta resolución"
                                >
                                  <img src={comp.archivoUrl} alt={`Comprobante Casa ${comp.casa}`} className="pending-thumb-img" />
                                  <div className="pending-thumb-overlay">
                                    <FaEye /> <span>Inspeccionar</span>
                                  </div>
                                </div>
                              ) : (
                                <a href={comp.archivoUrl} target="_blank" rel="noopener noreferrer" className="pending-pdf-btn">
                                  <FaFileAlt /> <span>Abrir comprobante PDF</span>
                                </a>
                              )}
                            </div>
                          )}

                          <div className="pending-card-actions">
                            <button
                              type="button"
                              className="btn-capturar-pago-comp"
                              onClick={() => handleAbrirModalPago(comp)}
                              title={`Capturar pago para Casa ${comp.casa}`}
                            >
                              <FaCreditCard /> Capturar Pago
                            </button>
                            <button
                              type="button"
                              className="btn-conciliado-comp"
                              onClick={() => handleMarcarConciliado(comp.id, comp.casa)}
                              title="Marcar como ya atendido/conciliado"
                            >
                              <FaCheck /> Conciliado
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="domus-ai-body">

                {/* Lista de Mensajes */}
                <div className="domus-ai-messages">
                  {messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`domus-ai-msg ${msg.sender === 'user' ? 'user-msg' : 'bot-msg'}`}
                    >
                      {msg.sender === 'bot' && (
                        <div className="domus-ai-msg-avatar">
                          <FaRobot />
                        </div>
                      )}
                      <div className="domus-ai-msg-content">
                        <div className="domus-ai-bubble">
                          {formatMessageText(msg.text)}
                        </div>

                        {/* Chips de Sugerencia Inteligente de Continuidad */}
                        {msg.sender === 'bot' && Array.isArray(msg.sugerencias) && msg.sugerencias.length > 0 && (
                          <div className="domus-ai-msg-sugerencias">
                            {msg.sugerencias.map((sug, sIdx) => (
                              <button
                                key={sug.id || sIdx}
                                type="button"
                                className="domus-ai-sug-btn"
                                onClick={() => handleAccionSugerencia(sug)}
                                disabled={isTyping || isUploading}
                                title={sug.tipo === 'navegacion' ? `Ir a ${sug.ruta}` : (sug.consulta || sug.texto)}
                              >
                                <span>{sug.texto}</span>
                                {sug.tipo === 'navegacion' && <FaExternalLinkAlt className="sug-nav-icon" />}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Barra de Acciones y Calificación de Respuesta (Estilo Syscom AI) */}
                        {msg.sender === 'bot' && msg.id !== 'welcome' && (
                          <div className="domus-ai-msg-actions-bar">
                            <button
                              type="button"
                              className="domus-ai-action-icon-btn"
                              onClick={() => handleCopiarMensaje(msg.text)}
                              title="Copiar respuesta"
                              aria-label="Copiar respuesta"
                            >
                              <FaCopy />
                            </button>

                            <button
                              type="button"
                              className={`domus-ai-action-icon-btn thumbs-btn ${calificaciones[msg.id] === 'buena' ? 'active-positive' : ''}`}
                              onClick={() => handleCalificar(msg, 'buena')}
                              disabled={Boolean(calificaciones[msg.id])}
                              title={calificaciones[msg.id] === 'buena' ? 'Calificado como buena respuesta' : 'Buena respuesta'}
                              aria-label="Buena respuesta"
                            >
                              <FaThumbsUp />
                            </button>

                            <button
                              type="button"
                              className={`domus-ai-action-icon-btn thumbs-btn ${calificaciones[msg.id] === 'mala' ? 'active-negative' : ''}`}
                              onClick={() => handleCalificar(msg, 'mala')}
                              disabled={Boolean(calificaciones[msg.id])}
                              title={calificaciones[msg.id] === 'mala' ? 'Calificado como mala respuesta' : 'Mala respuesta'}
                              aria-label="Mala respuesta"
                            >
                              <FaThumbsDown />
                            </button>
                          </div>
                        )}

                        <span className="domus-ai-msg-time">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="domus-ai-msg bot-msg">
                      <div className="domus-ai-msg-avatar">
                        <FaRobot />
                      </div>
                      <div className="domus-ai-typing-bubble">
                        <span className="dot" />
                        <span className="dot" />
                        <span className="dot" />
                      </div>
                    </div>
                  )}

                  {/* Uploading Indicator */}
                  {isUploading && (
                    <div className="domus-ai-msg bot-msg">
                      <div className="domus-ai-msg-avatar">
                        <FaSpinner className="domus-ai-spin" />
                      </div>
                      <div className="domus-ai-bubble" style={{ fontStyle: 'italic', color: '#475569' }}>
                        Subiendo comprobante de pago a la plataforma...
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Footer con Input */}
              <div className="domus-ai-footer">
                <div className="domus-ai-input-wrap">
                  {/* Botón Clip para adjuntar comprobante */}
                  <button
                    type="button"
                    className="domus-ai-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || isTyping}
                    title="Adjuntar comprobante de pago (Foto o PDF)"
                    aria-label="Adjuntar comprobante de pago"
                  >
                    <FaPaperclip />
                  </button>

                  <textarea
                    ref={inputRef}
                    rows="1"
                    className="domus-ai-input"
                    placeholder="Escribe tu consulta o duda..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isTyping || isUploading}
                  />
                  <button
                    className="domus-ai-send-btn"
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isTyping || isUploading}
                    aria-label="Enviar mensaje"
                  >
                    <FaPaperPlane />
                  </button>
                </div>
                <div className="domus-ai-footer-note">
                  <FaShieldAlt style={{ fontSize: '0.65rem', marginRight: '4px' }} />
                  Asistente oficial para residentes y administración • Domus Habita
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal Lightbox de Zoom para Inspección de Comprobantes */}
      {modalZoomImg && (
        <div className="domus-ai-modal-zoom-overlay" onClick={() => setModalZoomImg(null)}>
          <div className="domus-ai-modal-zoom-card" onClick={(e) => e.stopPropagation()}>
            <div className="domus-ai-modal-zoom-header">
              <div className="domus-ai-modal-zoom-title">
                <FaEye /> <span>Inspección de Comprobante de Pago</span>
              </div>
              <button 
                type="button" 
                className="domus-ai-modal-zoom-close" 
                onClick={() => setModalZoomImg(null)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="domus-ai-modal-zoom-body">
              <img src={modalZoomImg} alt="Comprobante en alta resolución" className="domus-ai-modal-zoom-img" />
            </div>
            <div className="domus-ai-modal-zoom-footer">
              <a href={modalZoomImg} target="_blank" rel="noopener noreferrer" className="domus-ai-btn-open-ext">
                Abrir en pestaña nueva ↗
              </a>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default DomusAIChatWidget;
